<template>
  <b-modal
    ref="modal"
    :title="modalTitle"
    @ok.prevent="submit"
    :ok-disabled="isOkDisabled"
    ok-title="Vista"
    cancel-title="Hætta við"
    :size="isSingleMode ? 'md' : 'xl'"
  >
    <b-alert show variant="warning" v-if="config.batch">
      <i class="fa fa-lock"></i>
      Ekki er hægt að breyta þessari færslu þar sem búið er að læsa henni vegna uppgjörs.
    </b-alert>

    <template v-if="isSingleMode">
      <b-form-group label="Dagsetning">
        <div>{{ form.rows[0].date | moment('DD.MM.YYYY') }}</div>
      </b-form-group>

      <b-form-group label="Tímar">
        <b-form-input
          v-model="form.rows[0].hours"
          type="text"
          :disabled="config.batch"
          :state="rowHoursInvalid(form.rows[0]) ? false : null"
        ></b-form-input>
        <b-form-invalid-feedback v-if="rowHoursInvalid(form.rows[0])">
          Hámarks fjöldi tíma á þessum degi er: {{ form.rows[0].maxHours }}.
        </b-form-invalid-feedback>
      </b-form-group>

      <b-form-group label="Veikindatímar" v-if="isVeikindi(form.rows[0])">
        <b-form-input v-model="form.rows[0].sick_hours" type="text" :disabled="config.batch"></b-form-input>
      </b-form-group>

      <b-form-group label="Verkefni" v-if="projectList.length > 0">
        <b-form-select v-model="form.rows[0].projectId" :disabled="config.batch">
          <option :value="null">-- Ekkert verkefni --</option>
          <option v-for="project in projectList" :key="project.id" :value="project.id">
            {{ project.name }}
          </option>
        </b-form-select>
      </b-form-group>

      <b-form-group label="Fjarvist">
        <b-form-select v-model="form.rows[0].absenceTypeId" :disabled="config.batch">
          <option :value="null">-- Engin fjarvist --</option>
          <option v-for="absence in absenceList" :key="absence.id" :value="absence.id">
            {{ absence.name }}
          </option>
        </b-form-select>
      </b-form-group>

      <b-form-group label="Athugasemd">
        <b-form-input v-model="form.rows[0].comment" type="text" :disabled="config.batch"></b-form-input>
      </b-form-group>
    </template>

    <template v-else>
      <b-row v-for="(row, index) in form.rows" :key="index" class="mb-2">
        <b-col cols="2">
          <b-form-group>
            <label :for="`date${index}`">
              Dagsetning
              <b-link @click.prevent="removeRow(index)" v-if="form.rows.length > 1">
                <i class="fa fa-trash"></i>
              </b-link>
            </label>
            <datepicker
              :id="`date${index}`"
              v-model="row.date"
              :language="lang"
              :monday-first="true"
              format="dd.MM.yyyy"
              :typeable="false"
              :disabled-dates="disabledDates"
              @input="onDateChange(row)"
            ></datepicker>
            <div v-if="row.loadingDate" class="mt-1">
              <b-spinner small></b-spinner>
            </div>
          </b-form-group>
        </b-col>

        <b-alert :show="!!row.invalidMessage && !row.loadingDate" style="padding-top: 20px" class="ml-2">
          <i class="fa fa-info-circle"></i>
          {{ row.invalidMessage }}
        </b-alert>

        <template v-if="!row.invalidMessage">
          <b-col cols="1">
            <b-form-group label="Tími" :state="rowHoursInvalid(row) ? false : null">
              <b-form-input
                type="text"
                v-model="row.hours"
                :class="rowHoursInvalid(row) ? 'text-danger' : ''"
                :disabled="isNonSickAbsence(row)"
              ></b-form-input>
            </b-form-group>
            <span v-if="rowHoursInvalid(row)">
              <i class="text-danger" style="font-size: small">Hámark: {{ row.maxHours }}.</i>
            </span>
          </b-col>

          <b-col cols="1" v-if="isVeikindi(row)">
            <b-form-group label="Veikindi">
              <b-form-input type="text" v-model="row.sick_hours"></b-form-input>
            </b-form-group>
          </b-col>

          <b-col :cols="projectList.length > 0 ? 2 : 0" v-if="projectList.length > 0">
            <b-form-group label="Verkefni">
              <b-form-select v-model="row.projectId" :disabled="isNonSickAbsence(row)">
                <option :value="null">-- Veldu verkefni --</option>
                <option v-for="project in projectList" :key="project.id" :value="project.id">
                  {{ project.name }}
                </option>
              </b-form-select>
            </b-form-group>
          </b-col>

          <b-col cols="3">
            <b-form-group label="Fjarvist">
              <b-form-select v-model="row.absenceTypeId" @input="onAbsenceChange(row)">
                <option :value="null">-- Veldu fjarvistartegund --</option>
                <option v-for="absence in absenceList" :key="absence.id" :value="absence.id">
                  {{ absence.name }}
                </option>
              </b-form-select>
            </b-form-group>
          </b-col>

          <b-col cols="3">
            <b-form-group label="Athugasemd">
              <b-form-input type="text" v-model="row.comment"></b-form-input>
            </b-form-group>
          </b-col>
        </template>
      </b-row>

      <b-button size="sm" @click="addRow" variant="default" :disabled="cannotAddRow">
        <i class="fa fa-fw fa-plus-circle"></i>
        Bæta við línu
      </b-button>
    </template>

    <div v-if="submitting" class="text-center mt-2">
      <b-spinner small></b-spinner>
      <span class="ml-2">Vista...</span>
    </div>

    <template slot="modal-ok">
      <b-spinner small v-if="submitting"></b-spinner>
      Vista
    </template>
  </b-modal>
</template>

<script>
import moment from 'moment';
import { mapActions } from 'vuex';
import Datepicker from 'vuejs-datepicker';
import { is } from 'vuejs-datepicker/dist/locale';
import attendance from '@/api/attendance';
import placement from '@/api/placement';
import { normalizeHoursInput } from '@/utils';

const VALIDATION_MESSAGES = {
  weekday: 'Þessi hópur á ekki að vinna á völdum degi.',
  vacationDay: 'Ekki er hægt að skrá viðveru þar sem búið er að skilgreina frídag fyrir valinn dag.',
  batch: 'Ekki er hægt að breyta færslum þar sem búið er að læsa þeim vegna uppgjörs.',
  duplicate: 'Ekki er hægt að skrá sömu dagsetningu tvisvar.',
};

const DEFAULT_CONFIG = {
  personId: null,
  personName: '',
  groupId: null,
  attendanceId: null,
  date: null,
  dateFrom: null,
  dateTo: null,
  hours: '',
  sick_hours: '',
  projectId: null,
  absenceTypeId: null,
  comment: '',
  maxHours: null,
  batch: false,
};

function defaultRow() {
  return {
    attendanceId: null,
    date: null,
    hours: '',
    sick_hours: '',
    projectId: null,
    absenceTypeId: null,
    comment: '',
    invalidMessage: null,
    loadingDate: false,
    maxHours: null,
  };
}

export default {
  name: 'attendance-edit-modal',
  components: { Datepicker },
  data() {
    return {
      config: { ...DEFAULT_CONFIG },
      form: {
        rows: [defaultRow()],
      },
      submitting: false,
      absenceList: [],
      projectList: [],
      lang: is,
    };
  },
  computed: {
    isSingleMode() {
      return !!this.config.attendanceId || !!this.config.date;
    },
    modalTitle() {
      return this.config.attendanceId ? `Breyta viðveru - ${this.config.personName}` : `Skrá viðveru - ${this.config.personName}`;
    },
    disabledDates() {
      if (this.config.dateFrom && this.config.dateTo) {
        return {
          to: moment(this.config.dateFrom).subtract(1, 'days').toDate(),
          from: moment(this.config.dateTo).add(1, 'days').toDate(),
        };
      }
      return {};
    },
    submittableRows() {
      return this.form.rows.filter(r => r.invalidMessage === null && r.date && !r.loadingDate && !this.rowHoursInvalid(r));
    },
    anyRowBusy() {
      return this.form.rows.some(r => r.loadingDate);
    },
    isOkDisabled() {
      return this.config.batch || this.submitting || this.anyRowBusy || this.submittableRows.length === 0;
    },
    cannotAddRow() {
      const last = this.form.rows[this.form.rows.length - 1];
      return !last || !last.date || last.loadingDate;
    },
  },
  methods: {
    isVeikindi(row) {
      if (!row.absenceTypeId) return false;
      const found = this.absenceList.find(a => a.id === row.absenceTypeId);
      return found ? found.is_sick_type : false;
    },
    isNonSickAbsence(row) {
      return row.absenceTypeId !== null && !this.isVeikindi(row);
    },
    rowHoursInvalid(row) {
      if (row.maxHours === null || row.hours === '' || row.hours === null) return false;
      const value = parseFloat(normalizeHoursInput(row.hours));
      if (Number.isNaN(value)) return false;
      return value > row.maxHours;
    },
    async show(config) {
      this.config = { ...DEFAULT_CONFIG, ...config };
      this.submitting = false;

      await this.loadDropdowns();

      const initialRow = {
        ...defaultRow(),
        attendanceId: config.attendanceId || null,
        date: config.date ? moment(config.date).toDate() : null,
        hours: config.hours != null ? String(config.hours) : '',
        sick_hours: config.sick_hours != null ? String(config.sick_hours) : '',
        projectId: config.projectId || null,
        absenceTypeId: config.absenceTypeId || null,
        comment: config.comment || '',
        maxHours: config.maxHours != null ? config.maxHours : null,
      };
      this.form = { rows: [initialRow] };

      if (!this.config.attendanceId && initialRow.date && !this.isSingleMode) {
        await this.onDateChange(initialRow);
      }

      this.$refs.modal.show();
    },
    async loadDropdowns() {
      if (this.absenceList.length > 0) return;
      try {
        const [absenceResponse, projectResponse] = await Promise.all([
          attendance.attendanceOverviewTypeList(),
          attendance.attendanceOverviewProjectList(),
        ]);
        this.absenceList = absenceResponse.data;
        this.projectList = projectResponse.data;
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa (${e.response ? e.response.status : -1}) kom upp. Vinsamlegast reyndu aftur.`);
      }
    },
    /* eslint-disable no-param-reassign */
    async onDateChange(row) {
      row.invalidMessage = null;
      if (!row.date || !this.config.groupId) {
        row.loadingDate = false;
        return;
      }

      row.loadingDate = true;
      try {
        const [attendanceResponse, validationResponse] = await Promise.all([
          attendance.attendanceListByPersonAndDate(this.config.personId, row.date),
          placement.placementGroupDateValid(this.config.groupId, row.date),
        ]);

        const validation = validationResponse.data;
        row.maxHours = validation.max_hours != null ? validation.max_hours : null;

        const rowKey = moment(row.date).format('YYYY-MM-DD');
        const duplicate = this.form.rows.some(r => r !== row && r.date && moment(r.date).format('YYYY-MM-DD') === rowKey);

        const rules = [
          [!validation.weekday_valid, VALIDATION_MESSAGES.weekday],
          [validation.vacation_day, VALIDATION_MESSAGES.vacationDay],
          [validation.batch, VALIDATION_MESSAGES.batch],
          [duplicate, VALIDATION_MESSAGES.duplicate],
        ];
        const failure = rules.find(([cond]) => cond);
        row.invalidMessage = failure ? failure[1] : null;

        const existing = attendanceResponse.data && attendanceResponse.data.length > 0 ? attendanceResponse.data[0] : null;
        if (existing) {
          row.attendanceId = existing.id;
          row.hours = existing.hours != null ? String(existing.hours) : '';
          row.sick_hours = existing.sick_hours != null ? String(existing.sick_hours) : '';
          row.projectId = existing.attendance_project ? existing.attendance_project.id : null;
          row.absenceTypeId = existing.attendance_type ? existing.attendance_type.id : null;
          row.comment = existing.comment || '';
        } else {
          row.attendanceId = null;
        }
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa (${e.response ? e.response.status : -1}) kom upp. Vinsamlegast reyndu aftur.`);
      } finally {
        row.loadingDate = false;
      }
    },
    onAbsenceChange(row) {
      if (this.isNonSickAbsence(row)) {
        row.hours = '';
        row.projectId = null;
      }
    },
    /* eslint-enable no-param-reassign */
    addRow() {
      const last = this.form.rows[this.form.rows.length - 1];
      const seedDate = last && last.date ? last.date : new Date();
      let nextDate = moment(seedDate).add(1, 'days').toDate();
      if (this.config.dateTo && moment(nextDate).isAfter(moment(this.config.dateTo))) {
        nextDate = this.config.dateFrom ? moment(this.config.dateFrom).toDate() : new Date();
      }
      const row = { ...defaultRow(), date: nextDate, loadingDate: true };
      this.form.rows.push(row);
      this.onDateChange(row);
    },
    removeRow(index) {
      this.form.rows.splice(index, 1);
    },
    async submit() {
      if (this.isOkDisabled) return;

      const rows = this.submittableRows;
      if (rows.length === 0) {
        this.displayError('Engar gildar línur til að vista.');
        return;
      }

      this.submitting = true;
      try {
        const results = await Promise.all(
          rows.map(row => {
            const sick = this.isVeikindi(row) ? normalizeHoursInput(row.sick_hours) ?? '0' : '0';
            const data = {
              date: moment(row.date).format('YYYY-MM-DD'),
              person: this.config.personId,
              hours: normalizeHoursInput(row.hours) ?? '0',
              sick_hours: sick,
              attendance_type: row.absenceTypeId || null,
              attendance_project: row.projectId || null,
              comment: row.comment || '',
            };
            const requestPromise = row.attendanceId
              ? attendance.attendanceUpdate(row.attendanceId, data).then(() => row.attendanceId)
              : attendance.attendanceCreate(data).then(res => res.data.id);
            return requestPromise.then(attendanceId => ({ row, data, attendanceId }));
          }),
        );

        const message =
          rows.length === 1
            ? `Viðvera fyrir „${this.config.personName}" hefur verið vistuð.`
            : `${rows.length} viðverufærslur fyrir „${this.config.personName}" hafa verið vistaðar.`;
        this.displaySuccess(message);

        if (this.isSingleMode && results.length === 1) {
          const { row, data, attendanceId } = results[0];
          this.$emit('successful', {
            personId: this.config.personId,
            attendanceId,
            date: data.date,
            hours: data.hours,
            sick_hours: data.sick_hours,
            attendance_type: row.absenceTypeId ? this.absenceList.find(a => a.id === row.absenceTypeId) || null : null,
            attendance_project: row.projectId ? this.projectList.find(p => p.id === row.projectId) || null : null,
            comment: data.comment,
          });
        } else {
          this.$emit('successful', { personId: this.config.personId });
        }
        this.$refs.modal.hide();
      } catch (e) {
        this.$log.error(e);
        this.displayError(`Óvænt villa (${e.response ? e.response.status : -1}) kom upp. Vinsamlegast reyndu aftur.`);
      } finally {
        this.submitting = false;
      }
    },
    ...mapActions({
      displayError: 'displayError',
      displaySuccess: 'displaySuccess',
    }),
  },
};
</script>

<style scoped></style>
